import { useState, useEffect, useContext } from "react"
import { AuthContext } from "../context/AuthContext"
import UpdateOrderModal from "../components/UpdateOrderModal"
import OrderDetailsModal from "../components/OrderDetailsModal"
import DeleteOrderModal from "../components/DeleteOrderModal"
import axios from "axios"
import { format, startOfDay, endOfDay, subDays, startOfMonth, endOfMonth, subMonths, isWithinInterval, parseISO } from "date-fns"
import { Eye, Edit, Search, FileSpreadsheet, Filter, X, Calendar, Trash2 } from "lucide-react"
import { showToast } from "../utils/toastUtils"

import * as XLSX from "xlsx"
import { saveAs } from "file-saver"

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000"

const OrdersList = () => {
  const { token } = useContext(AuthContext)

  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [selectedOrder, setSelectedOrder] = useState(null)
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false)

  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [selectedOrderId, setSelectedOrderId] = useState(null)

  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilters, setStatusFilters] = useState([])
  const [filteredOrders, setFilteredOrders] = useState([])

  // Date Filtering State
  const [timeFilter, setTimeFilter] = useState("all") // all, today, yesterday, last7, last30, thisMonth, lastMonth, custom
  const [dateRange, setDateRange] = useState({ start: "", end: "" })

  const [exporting, setExporting] = useState(false)
  const [syncing, setSyncing] = useState(false)

  // Reusable Input Style
  const inputStyle = "w-full rounded-lg border-gray-200 bg-white px-4 py-2.5 text-gray-700 placeholder-gray-400 shadow-sm transition-all duration-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none hover:border-blue-300"

  if (token) {
    axios.defaults.headers.common["x-auth-token"] = token
  }

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true)
        const res = await axios.get(`${BASE_URL}/api/orders`)
        const sortedOrders = res.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setOrders(sortedOrders)
        setFilteredOrders(sortedOrders)
        setError(null)
      } catch (err) {
        const msg = err.response?.data?.message || "Error fetching orders"
        setError(msg)
        showToast("Error", msg, "error")
      } finally {
        setLoading(false)
      }
    }
    fetchOrders()
  }, [])

  // Handle Time Filter Presets
  const handleTimeFilter = (filter) => {
    setTimeFilter(filter)
    const now = new Date()
    let start = null
    let end = null

    switch (filter) {
      case "today":
        start = startOfDay(now)
        end = endOfDay(now)
        break
      case "yesterday":
        const yest = subDays(now, 1)
        start = startOfDay(yest)
        end = endOfDay(yest)
        break
      case "last7":
        start = startOfDay(subDays(now, 6))
        end = endOfDay(now)
        break
      case "last30":
        start = startOfDay(subDays(now, 29))
        end = endOfDay(now)
        break
      case "thisMonth":
        start = startOfMonth(now)
        end = endOfDay(now) // or endOfMonth(now)
        break
      case "lastMonth":
        const lastM = subMonths(now, 1)
        start = startOfMonth(lastM)
        end = endOfMonth(lastM)
        break
      case "custom":
        // Keep existing custom dates if available, else don't reset immediately
        return
      case "all":
      default:
        start = null
        end = null
        break
    }

    if (filter !== "custom") {
      setDateRange({
        start: start ? format(start, "yyyy-MM-dd") : "",
        end: end ? format(end, "yyyy-MM-dd") : ""
      })
    }
  }

  // Handle Custom Date Input
  const handleCustomDateChange = (type, value) => {
    setTimeFilter("custom")
    setDateRange(prev => ({ ...prev, [type]: value }))
  }

  useEffect(() => {
    let filtered = [...orders]

    // 1. Date Filter
    if (dateRange.start || dateRange.end) {
      filtered = filtered.filter((order) => {
        if (!order.createdAt) return false
        const orderDate = new Date(order.createdAt)

        const start = dateRange.start ? startOfDay(parseISO(dateRange.start)) : null
        const end = dateRange.end ? endOfDay(parseISO(dateRange.end)) : null

        if (start && end) {
          return isWithinInterval(orderDate, { start, end })
        } else if (start) {
          return orderDate >= start
        } else if (end) {
          return orderDate <= end
        }
        return true
      })
    }

    // 2. Status Filter
    if (statusFilters.length > 0) {
      filtered = filtered.filter((order) => statusFilters.includes(order.status))
    }

    // 3. Search Filter
    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (order) =>
          order.customerName?.toLowerCase().includes(lower) ||
          order.phone?.includes(searchTerm) ||
          (order.trackingNumber && order.trackingNumber.toLowerCase().includes(lower)) ||
          (order.courierServiceId?.name && order.courierServiceId.name.toLowerCase().includes(lower)) ||
          (order.orderId && order.orderId.toLowerCase().includes(lower))
      )
    }

    setFilteredOrders(filtered)
  }, [searchTerm, statusFilters, orders, dateRange])

  const handleUpdateOrder = async (orderId, updatedData) => {
    try {
      const res = await axios.put(`${BASE_URL}/api/orders/${orderId}/status`, updatedData)
      setOrders((prev) => prev.map((o) => (o._id === orderId ? { ...o, ...res.data } : o)))
      setIsUpdateModalOpen(false)
      showToast("Success", "Order updated successfully", "success")
      return true
    } catch (err) {
      showToast("Error", err.response?.data?.message || "Error updating order", "error")
      return false
    }
  }

  const handleViewOrderDetails = (orderId) => {
    setSelectedOrderId(orderId)
    setIsDetailsModalOpen(true)
  }

  // Delete State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [orderToDelete, setOrderToDelete] = useState(null)

  const confirmDelete = (order) => {
    setOrderToDelete(order)
    setIsDeleteModalOpen(true)
  }

  const handleDeleteOrder = async (orderId, reason) => {
    try {
      await axios.delete(`${BASE_URL}/api/orders/${orderId}`, { data: { reason } })
      setOrders(prev => prev.filter(o => o._id !== orderId))
      setFilteredOrders(prev => prev.filter(o => o._id !== orderId))
      showToast("Success", "Order deleted and items restocked", "success")
    } catch (err) {
      console.error(err)
      showToast("Error", err.response?.data?.message || "Failed to delete order", "error")
    }
  }

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "New":
        return "bg-amber-100 text-amber-800 border-amber-200"
      case "Pending Packed":
        return "bg-blue-100 text-blue-800 border-blue-200"
      case "Pending Confirm":
        return "bg-purple-100 text-purple-800 border-purple-200"
      case "Packed":
        return "bg-indigo-100 text-indigo-800 border-indigo-200"
      case "Handover to Courier":
        return "bg-cyan-100 text-cyan-800 border-cyan-200"
      case "Delivered":
        return "bg-green-100 text-green-800 border-green-200"
      case "Cancelled":
        return "bg-red-100 text-red-800 border-red-200"
      case "Returned":
        return "bg-rose-100 text-rose-800 border-rose-200"
      case "Postponed":
        return "bg-orange-100 text-orange-800 border-orange-200"
      default:
        return "bg-gray-100 text-gray-800 border-gray-200"
    }
  }

  const formatAddress = (order) => {
    const addressParts = [
      order.addressLine1,
      order.addressLine2,
      order.city,
    ].filter(Boolean)
    return addressParts.join(", ")
  }

  // --------------------------
  // Export helpers
  // --------------------------
  const fetchOrderDetails = async (id) => {
    const { data } = await axios.get(`${BASE_URL}/api/orders/${id}`)
    return data // { order, orderLines: [...] }
  }

  const groupOrderLines = (lines = []) => {
    const map = new Map()
    for (const line of lines) {
      const key = `${line.productName || "Item"}|||${line.variation || ""}`
      const existing = map.get(key) || {
        productName: line.productName || "Item",
        variation: line.variation || "",
        quantity: 0,
        lineTotal: 0,
      }
      existing.quantity += Number(line.quantity || 0)
      existing.lineTotal += Number(line.lineTotal || 0)
      map.set(key, existing)
    }
    return Array.from(map.values())
  }

  const buildRowsForOrder = (order, groupedItems, totals) => {
    // ... logic remains unchanged
    const base = {
      "Order ID": order.orderId || "-",
      "Order Date": order.createdAt ? format(new Date(order.createdAt), "MMM d, yyyy") : "-",
      Customer: order.customerName || "-",
      Phone: [order.phone, order.phone2].filter(Boolean).join(" / ") || "-",
      Address: formatAddress(order) || "-",
      Status: order.status || "-",
      Courier: order.courierServiceId?.name || "-",
      "Tracking No": order.trackingNumber || "-",
      "Shipping (Rs.)": Number(totals.shipping || 0),
      "Grand Total (Rs.)": Number(totals.grandTotal || 0),
    }

    if (!groupedItems.length) {
      return [
        {
          ...base,
          Product: "(items unavailable)",
          Variation: "",
          Quantity: 0,
          "MRP (Rs.)": 0,
          "Item Total (Rs.)": 0,
        },
      ]
    }

    return groupedItems.map((g) => {
      const qty = Number(g.quantity || 0)
      const line = Number(g.lineTotal || 0)
      const mrp = qty ? Math.round((line / qty) * 100) / 100 : 0
      return {
        ...base,
        Product: g.productName || "Item",
        Variation: g.variation || "",
        Quantity: qty,
        "MRP (Rs.)": mrp,
        "Item Total (Rs.)": Math.round(line * 100) / 100,
      }
    })
  }

  const handleSyncWooCommerce = async () => {
    try {
      setSyncing(true)
      const res = await axios.get(`${BASE_URL}/api/orders/sync-woocommerce`)
      if (res.data.syncedCount > 0) {
        showToast("Sync Completd", `Imported ${res.data.syncedCount} new missing WooCommerce orders.`, "success")
        // Refresh orders list
        const latestRes = await axios.get(`${BASE_URL}/api/orders`)
        const sortedOrders = latestRes.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setOrders(sortedOrders)
        setFilteredOrders(sortedOrders)
      } else {
        showToast("Already Up To Date", "No new missing WooCommerce orders found.", "info")
      }
    } catch (err) {
      showToast("Sync Failed", err.response?.data?.message || "Failed to sync WooCommerce orders", "error")
    } finally {
      setSyncing(false)
    }
  }

  const exportToExcel = async () => {
    try {
      setExporting(true)

      const allRows = []

      let globalThreshold = 2000
      let defaultShippingCost = 350
      try {
        const [thresholdRes, shippingRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/settings/freeDeliveryThreshold`),
          axios.get(`${BASE_URL}/api/settings/defaultShippingCost`)
        ])
        if (thresholdRes.data && thresholdRes.data.value) {
          globalThreshold = parseFloat(thresholdRes.data.value)
        }
        if (shippingRes.data && shippingRes.data.value) {
          defaultShippingCost = parseFloat(shippingRes.data.value)
        }
      } catch (err) {
        console.warn("Failed to fetch global settings for export", err)
      }

      // Use filteredOrders here (which includes date, search, and status filters)
      for (const order of filteredOrders) {
        try {
          const full = await fetchOrderDetails(order._id)
          const grouped = groupOrderLines(full.orderLines || [])

          const sub = Number(full.order?.subTotal ?? 0)
          const ship = sub >= globalThreshold ? 0 : (full.order?.originalShippingCost || defaultShippingCost)
          const total = sub + ship

          const rows = buildRowsForOrder(order, grouped, {
            shipping: ship,
            grandTotal: total,
          })
          allRows.push(...rows)
        } catch {
          allRows.push({
            "Order ID": order.orderId || "-",
            "Order Date": order.createdAt ? format(new Date(order.createdAt), "MMM d, yyyy") : "-",
            Customer: order.customerName || "-",
            Phone: [order.phone, order.phone2].filter(Boolean).join(" / ") || "-",
            Address: formatAddress(order) || "-",
            Status: order.status || "-",
            Courier: order.courierServiceId?.name || "-",
            "Tracking No": order.trackingNumber || "-",
            "Shipping (Rs.)": "",
            "Grand Total (Rs.)": "",
            "Product": "(items unavailable)",
            "Variation": "",
            "Quantity": 0,
            "MRP (Rs.)": 0,
            "Item Total (Rs.)": 0,
          })
        }
      }

      const ws = XLSX.utils.json_to_sheet(allRows)

      const headers = Object.keys(allRows[0] || {})
      ws["!cols"] = headers.map((h) => {
        const min =
          h === "Product" ? 40 :
            h === "Address" ? 30 :
              16
        return { wch: Math.max(h.length + 2, min) }
      })

      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, "Orders")
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" })
      const filename = `orders_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`
      saveAs(new Blob([wbout], { type: "application/octet-stream" }), filename)
      showToast("Export Successful", `Exported ${filteredOrders.length} orders to Excel`, "success")
    } catch (err) {
      console.error(err)
      showToast("Export Failed", "Failed to export to Excel", "error")
    } finally {
      setExporting(false)
    }
  }

  const toggleStatusFilter = (status) => {
    setStatusFilters((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    )
  }

  const STATUSES = ["New", "Pending Confirm", "Pending Packed", "Packed", "Handover to Courier", "Delivered", "Returned", "Cancelled", "Postponed"];

  const TIME_FILTERS = [
    { id: "all", label: "All Time" },
    { id: "today", label: "Today" },
    { id: "yesterday", label: "Yesterday" },
    { id: "last7", label: "Last 7 Days" },
    { id: "last30", label: "Last 30 Days" },
    { id: "thisMonth", label: "This Month" },
    { id: "lastMonth", label: "Last Month" },
  ]


  return (
    <div className="flex bg-gray-50 min-h-screen pb-12">
      <div className="flex-1 flex flex-col w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">All Orders Master</h1>
            <p className="mt-1 text-sm text-gray-500">Comprehensive view and management of all cross-platform orders.</p>
          </div>
          <div className="flex justify-end gap-3 self-end w-full sm:w-auto">
            <button
              onClick={handleSyncWooCommerce}
              disabled={syncing}
              className={`flex-1 sm:flex-none justify-center inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl text-white transition-all duration-200 shadow-sm ${syncing
                ? "bg-purple-400 cursor-not-allowed border border-transparent"
                : "bg-purple-600 hover:bg-purple-700 hover:shadow-md hover:-translate-y-0.5"
                }`}
            >
              <svg
                className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {syncing ? "Syncing..." : "Sync WooCommerce"}
            </button>

            <button
              onClick={exportToExcel}
              disabled={exporting}
              className={`flex-1 sm:flex-none justify-center inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 shadow-sm border ${exporting
                ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:shadow-md hover:-translate-y-0.5"
                }`}
            >
              <FileSpreadsheet className={`w-4 h-4 ${exporting ? 'animate-pulse' : 'text-green-600'}`} />
              {exporting ? "Exporting..." : "Export to Excel"}
            </button>
          </div>
        </div>

        {/* Filters & Search - Combined Block */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6 space-y-6">

          {/* 1. Date Filters */}
          <div>
            <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-gray-700">
              <Calendar className="w-4 h-4" />
              Filter by Date
            </div>
            <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
              {/* Presets */}
              <div className="flex flex-wrap gap-2">
                {TIME_FILTERS.map((tf) => (
                  <button
                    key={tf.id}
                    onClick={() => handleTimeFilter(tf.id)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-all duration-200 font-medium ${timeFilter === tf.id
                      ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                  >
                    {tf.label}
                  </button>
                ))}
              </div>

              {/* Custom Date Inputs */}
              <div className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg border border-gray-200">
                <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Custom Range:</span>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => handleCustomDateChange("start", e.target.value)}
                  className="text-xs border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 bg-white"
                />
                <span className="text-gray-400">-</span>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => handleCustomDateChange("end", e.target.value)}
                  className="text-xs border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 bg-white"
                />
              </div>
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* 2. Search & Status */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Search */}
            <div className="flex-1 relative group">
              <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
              <input
                type="text"
                className={`${inputStyle} pl-10`}
                placeholder="Search by order ID, customer, phone, tracking..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Status Filters */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                <Filter className="w-4 h-4" />
                Filter by Status
              </div>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((status) => (
                  <button
                    key={status}
                    onClick={() => toggleStatusFilter(status)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-all duration-200 ${statusFilters.includes(status)
                      ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                  >
                    {status}
                  </button>
                ))}
                {(statusFilters.length > 0 || timeFilter !== 'all' || dateRange.start || dateRange.end) && (
                  <button
                    onClick={() => {
                      setStatusFilters([])
                      handleTimeFilter('all')
                      setSearchTerm("")
                    }}
                    className="text-xs px-2 py-1.5 text-red-600 hover:text-red-700 font-medium flex items-center gap-1 ml-2"
                  >
                    <X className="w-3 h-3" /> Clear All
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Orders Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mb-4"></div>
              <p className="text-gray-500">Loading orders...</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-4">
              <div className="bg-gray-100 rounded-full p-4 mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">No Orders Found</h3>
              <p className="text-gray-500 mt-1 max-w-sm">
                Try adjusting your search or filters to find what you're looking for.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[100px]">Order ID</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[120px]">Date</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[180px]">Customer</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[280px]">Address</th>
                    <th className="px-4 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[120px]">Total</th>
                    <th className="px-4 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[140px]">Status</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[180px]">Courier / Tracking</th>
                    <th className="px-4 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[140px] sticky right-0 bg-gray-50 z-20 shadow-[-2px_0_5px_rgba(0,0,0,0.05)]">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders.map((order) => (
                    <tr key={order._id} className="hover:bg-blue-50/30 transition-colors duration-150 group">
                      <td className="px-4 py-4 whitespace-nowrap align-top">
                        <div className="text-sm font-bold text-gray-900">#{order.orderId || "-"}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 align-top">
                        {order.createdAt ? format(new Date(order.createdAt), "MMM d, yyyy") : "-"}
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900 line-clamp-2" title={order.customerName}>{order.customerName}</span>
                          <span className="text-xs text-gray-500">{order.phone}</span>
                          {order.phone2 && <span className="text-xs text-gray-400">{order.phone2}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <p className="text-sm text-gray-600 line-clamp-3" title={formatAddress(order)}>
                          {formatAddress(order)}
                        </p>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right align-top">
                        <span className="text-sm font-semibold text-gray-900">
                          Rs. {order.total?.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center align-top whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border text-center ${getStatusBadgeColor(order.status)}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-700 font-medium break-words" title={order.courierServiceId?.name}>
                            {order.courierServiceId?.name || "-"}
                          </span>
                          {order.trackingNumber && (
                            <span className="text-xs text-gray-500 font-mono mt-0.5 bg-gray-100 px-1.5 py-0.5 rounded w-fit break-all" title={order.trackingNumber}>
                              {order.trackingNumber}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium align-top sticky right-0 bg-white group-hover:bg-blue-50/30 z-10 shadow-[-2px_0_5px_rgba(0,0,0,0.05)]">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => {
                              setSelectedOrder(order)
                              setIsUpdateModalOpen(true)
                            }}
                            className="p-2 bg-white border border-gray-200 rounded-lg text-blue-600 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 transition-all shadow-sm group-hover:shadow-md"
                            title="Edit Order"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleViewOrderDetails(order._id)}
                            className="p-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900 transition-all shadow-sm group-hover:shadow-md"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => confirmDelete(order)}
                            className="p-2 bg-white border border-gray-200 rounded-lg text-red-600 hover:bg-red-50 hover:border-red-200 hover:text-red-700 transition-all shadow-sm group-hover:shadow-md"
                            title="Delete Order"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>

      {selectedOrder && (
        <UpdateOrderModal
          isOpen={isUpdateModalOpen}
          onClose={() => setIsUpdateModalOpen(false)}
          order={selectedOrder}
          onUpdate={handleUpdateOrder}
        />
      )}

      <OrderDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        orderId={selectedOrderId}
        token={token}
      />

      {orderToDelete && (
        <DeleteOrderModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          order={orderToDelete}
          onDelete={handleDeleteOrder}
        />
      )}
    </div>
  )
}

export default OrdersList
